import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Container, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { payoutAPI } from '../utils/api';
import type { NurseryPayout } from '../types/billing';

const statusVariant: Record<NurseryPayout['status'], string> = {
  pending: 'warning',
  paid: 'success',
  failed: 'danger',
};

const formatMoney = (amount: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format((amount || 0) / 100);

const formatDate = (value: string | null) => value ? new Date(value).toLocaleDateString() : '-';

const NurseryPayouts: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'payouts' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const { canViewBilling } = useRolePermissions();
  const nurseryId = currentNursery?.nursery_id || '';
  const [payouts, setPayouts] = useState<NurseryPayout[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!nurseryId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await payoutAPI.list(nurseryId);
      setPayouts(response.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [nurseryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const totals = useMemo(() => payouts.reduce((acc, payout) => {
    acc.totalRevenue += payout.total_revenue || 0;
    acc.nurseryShare += payout.nursery_share || 0;
    return acc;
  }, { totalRevenue: 0, nurseryShare: 0 }), [payouts]);

  if (!canViewBilling) {
    return <Layout><Container fluid className="py-4"><Alert variant="danger">{t('noAccess')}</Alert></Container></Layout>;
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div><h2 className="fw-bold text-primary mb-0">{t('title')}</h2></div>
        </div>
        {currentNursery ? (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: tc('nav.payouts').toLowerCase() })}</strong> {currentNursery.description || currentNursery.name} ({tc('labels.id')}: {currentNursery.nursery_id})
          </Alert>
        ) : (
          <Alert variant="warning">{t('selectNursery')}</Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="d-flex flex-wrap gap-3 mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="text-muted small">{t('totalRevenue')}</div>
              <div className="h4 mb-0">{formatMoney(totals.totalRevenue)}</div>
            </Card.Body>
          </Card>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="text-muted small">{t('nurseryShare')}</div>
              <div className="h4 mb-0">{formatMoney(totals.nurseryShare)}</div>
            </Card.Body>
          </Card>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light"><tr><th>{t('month')}</th><th>{t('totalRevenue')}</th><th>{t('nurseryShare')}</th><th>{tc('labels.status')}</th><th>{t('transferId')}</th><th>{t('paidAt')}</th><th>{tc('labels.created')}</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-5"><Spinner animation="border" /></td></tr> : payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td>{payout.month}</td>
                    <td>{formatMoney(payout.total_revenue)}</td>
                    <td>{formatMoney(payout.nursery_share)}</td>
                    <td><Badge bg={statusVariant[payout.status]}>{t(`statuses.${payout.status}`, { defaultValue: payout.status })}</Badge></td>
                    <td>{payout.stripe_transfer_id ? <code>{payout.stripe_transfer_id}</code> : '-'}</td>
                    <td>{formatDate(payout.paid_at)}</td>
                    <td>{formatDate(payout.created_at)}</td>
                  </tr>
                ))}
                {!loading && payouts.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-5">{t('noPayouts')}</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default NurseryPayouts;
